import { Injectable, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import {
  BaseRequest,
  Channel,
  CurrentEditor,
  Subscription,
  Entry
} from '../../interfaces/notification.interface';
import { AuthenticationService } from '../authentication/authentication.service';
import { AppStateService } from '../app-state/app-state.service';
import { Router } from '@angular/router';
import { WSS_API_BASE, NOTIFICATION } from '../../constants/general.constants';
import * as AppConfigModule from '../../../../config-loader';
import { RTMessagingClient } from '../asyncapi-websocket/rt-messaging-client';
import { WebSocketClientState } from '../asyncapi-websocket/base-asyncapi-client';
import {
  ChannelWithEvents as GeneratedChannel,
  SubscriptionInfo as GeneratedSubscription,
  EntryInfo as GeneratedEntry,
  CurrentEditorInfo as GeneratedCurrentEditor,
  Event as GeneratedEvent,
} from '@app/shared/interfaces/rt-messaging.generated';

// WebSocket error classification types
enum WebSocketErrorType {
  CONFIGURATION = 'configuration',
  NETWORK = 'network',
  RUNTIME = 'runtime'
}

// Union type for all possible WebSocket message types (for backward compatibility)
type WebSocketResponse =
  | {
    message: 'Error';
    trans_id?: number;
    details?: {
      description: string;
      [key: string]: unknown;
    };
  }
  | {
    message: 'channel-list';
    trans_id?: number;
    channels: Channel[];
  }
  | {
    message: 'current-editor';
    trans_id?: number;
    editor: CurrentEditor;
  }
  | {
    message: 'list-subscriptions';
    trans_id?: number;
    subscriptions: Subscription[];
  }
  | {
    message: 'get-entries' | 'new-entries';
    trans_id?: number;
    entries: Entry[];
  }
  | {
    message: 'subscribe' | 'unsubscribe' | 'watch-editors' | 'lock-editor' | 'unlock-editor';
    trans_id?: number;
    [key: string]: unknown;
  }
  | {
    message: string; // fallback for other types
    trans_id?: number;
    [key: string]: unknown;
  };

// Type guard functions for better type safety
function _isErrorMessage(data: WebSocketResponse): data is Extract<WebSocketResponse, { message: 'Error' }> {
  return data.message === 'Error';
}

function _isChannelListMessage(data: WebSocketResponse): data is Extract<WebSocketResponse, { message: 'channel-list' }> {
  return data.message === 'channel-list';
}


@Injectable({
  providedIn: 'root',
})
export class NotificationWebsocketService {
  private readonly toastr = inject(ToastrService);
  private readonly authService = inject(AuthenticationService);
  private readonly appStateService = inject(AppStateService);
  private readonly router = inject(Router);

  private get WS_URL(): string {
    return `${WSS_API_BASE}/${NOTIFICATION}`;
  }

  private get isEnabled(): boolean {
    return !!WSS_API_BASE && WSS_API_BASE !== 'undefined' && WSS_API_BASE !== '';
  }

  /** New type-safe RT Messaging client */
  private client: RTMessagingClient | null = null;

  /** Legacy message subject for backward compatibility */
  public messages: Subject<WebSocketResponse> = new Subject<WebSocketResponse>();

  /** Client state observable */
  public readonly state$ = new BehaviorSubject<WebSocketClientState>(WebSocketClientState.INITIAL);

  private readonly AUTH_RETRY_INTERVAL_TIME = 5000;
  private subscribedEventIds = new Set<number>();
  private notifiedEntryIds = new Set<number>(); // Track entry IDs that have been notified about
  private authRetryInterval: any;

  public async init() {
    // Check if WebSocket is enabled before attempting connection
    if (!this.isEnabled) {
      console.log('ℹ️ Notification WebSocket disabled - no valid WSS_API_BASE configured');
      return;
    }

    try {
      // Use access token (not ID token) for WebSocket authentication
      // This matches what the HTTP interceptor uses for API requests
      const token = this.authService.getAccessToken();
      if (!token) {
        console.log('No access token available yet, will retry initialization later');
        this.setupAuthRetry();
        return;
      }
      this.clearAuthRetry();
      await this.setConnection(token);
    } catch (error) {
      console.error('Failed to initialize websocket connection:', error);
      this.setupAuthRetry();
    }
  }

  private setupAuthRetry(): void {
    if (!this.authRetryInterval) {
      console.log(`Setting up auth retry interval (${this.AUTH_RETRY_INTERVAL_TIME}ms)`);
      this.authRetryInterval = setInterval(() => {
        this.retryInit();
      }, this.AUTH_RETRY_INTERVAL_TIME);
    }
  }

  private clearAuthRetry(): void {
    if (this.authRetryInterval) {
      console.log('Clearing auth retry interval');
      clearInterval(this.authRetryInterval);
      this.authRetryInterval = null;
    }
  }

  private async retryInit(): Promise<void> {
    console.log('Retrying websocket initialization...');
    try {
      const token = this.authService.getAccessToken();
      if (token) {
        console.log('Token obtained, initializing websocket connection');
        this.clearAuthRetry();
        await this.setConnection(token);
      }
    } catch (error) {
      console.error('Error during websocket init retry:', error);
    }
  }

  private async setConnection(token: string): Promise<void> {
    // Use the main /api/ws endpoint (not /api/ws/{user_id} which is test-only)
    // The server determines the user from the token
    // Note: Browser WebSocket API doesn't support custom headers, so we pass
    // the token as a query parameter. The server must be configured to accept
    // tokens via query string for WebSocket connections.
    const url = `${this.WS_URL}`;
    console.log(`Setting up WebSocket connection to ${this.WS_URL}`);

    // Create new RT Messaging client with authentication token
    // The token will be automatically appended as a query parameter
    this.client = new RTMessagingClient(url, {
      debug: !AppConfigModule.appConfig.env.production,
      requestTimeout: 10000,
      reconnectDelay: 2000,
      maxReconnectAttempts: 5,
      authToken: token, // Pass token for authentication
    });

    // Subscribe to client state changes
    this.client.state$.subscribe((state) => {
      this.state$.next(state);

      if (state === WebSocketClientState.FAILED) {
        this.handleConnectionFailure('Unable to maintain websocket connection');
      }
    });

    // Subscribe to all messages for backward compatibility
    // Note: data-changed messages are excluded here because they are handled
    // via the dedicated dataChanges subscription below to prevent double-emission.
    this.client.messages$.subscribe((message) => {
      // Skip data-changed and job-event messages — they flow through their
      // dedicated dataChanges / jobEvents paths below to avoid double-emission.
      const msgType = (message as any).message;
      if (msgType === 'data-changed' || msgType === 'job-event') {
        return;
      }

      // Convert to legacy format and emit
      this.messages.next(message as unknown as WebSocketResponse);

      // Handle errors
      if ((message as any).message === 'Error') {
        const errorMsg = message as any;
        if (errorMsg.details) {
          const ignoreErrors = ['editor locked'];
          if (!ignoreErrors.includes(errorMsg.details.description)) {
            this.toastr.error(errorMsg.details.description, 'Error!');
          }
        }
      }

      // Handle invite-related notifications - only for new, unseen entries
      this.handleInviteNotifications(message);
    });

    // Subscribe to new entries push notifications
    this.client.newEntries$.subscribe((entries) => {
      console.log('Received new entries:', entries);
      const convertedEntries = this.convertEntries(entries);
      
      // Check for new, unseen invite entries and show notification
      const newInviteEntries = convertedEntries.filter(entry => 
        this.isInviteEntry(entry) && 
        !entry.entrySeen && 
        !this.notifiedEntryIds.has(entry.entryId)
      );

      if (newInviteEntries.length > 0) {
        this.toastr.info('You have a new project invitation or an invite was updated', 'Invitations');
        // Track these entries as notified
        newInviteEntries.forEach(entry => {
          this.notifiedEntryIds.add(entry.entryId);
        });
      }

      // Emit as legacy message format
      this.messages.next({
        message: 'new-entries',
        entries: convertedEntries,
      });
    });

    // Subscribe to editor changes
    this.client.editorChanges$.subscribe((editor) => {
      console.log('Received editor change:', editor);
      // Emit as legacy message format
      this.messages.next({
        message: 'current-editor',
        editor: this.convertCurrentEditor(editor),
      });
    });

    // Subscribe to project data changes
    this.client.dataChanges.subscribe((data) => {
      this.messages.next({
        message: 'data-changed',
        project_id: data.projectId,
        change_type: data.changeType,
        // SCRUM-751: preserve the workflow id for workflow-scoped change types.
        context_id: data.contextId,
      });
    });

    // Subscribe to job/step execution events (SCRUM-788) and re-emit onto the
    // legacy message stream (snake_case wire shape) so consumers such as
    // JobWatchService can filter by job_id via receiveMessage().
    this.client.jobEvents.subscribe((event) => {
      this.messages.next({
        message: 'job-event',
        kind: event.kind,
        project_id: event.projectId,
        routine_id: event.routineId,
        job_id: event.jobId,
        status: event.status,
        exec_step_id: event.execStepId,
        step_id: event.stepId,
        step_index: event.stepIndex,
      });
    });

    try {
      // Connect to the server
      await this.client.connect();
      console.log('WebSocket connection established');

      // Setup initial subscriptions
      await this.onConnected();
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      this.handleNetworkError(error);
    }
  }

  private async onConnected(): Promise<void> {
    if (!this.client) return;

    // Watch editor changes
    await this.watchEditors();

    // Subscribe to invite-related events
    await this.subscribeToInviteNotifications();
  }

  private isInviteRelated(msg: any): boolean {
    try {
      const text = JSON.stringify(msg).toLowerCase();
      return text.includes('invite');
    } catch {
      return false;
    }
  }

  /**
   * Check if an entry is invite-related based on event/channel name
   */
  private isInviteEntry(entry: Entry): boolean {
    if (!entry) return false;
    const eventName = (entry.eventName || '').toLowerCase();
    const channelName = (entry.channelName || '').toLowerCase();
    return eventName.includes('invite') || channelName.includes('invite');
  }

  /**
   * Handle invite-related notifications from messages
   * Only shows notifications for new, unseen invite entries
   * Note: new-entries are handled directly in newEntries$ subscription to avoid duplicates
   */
  private handleInviteNotifications(message: any): void {
    // For error messages with invite-related content, show them
    if ((message as any).message === 'Error' && (message as any).details) {
      const errorMsg = message as any;
      if (this.isInviteRelated(errorMsg)) {
        this.toastr.info(errorMsg.details.description || 'You have a new project invitation or an invite was updated', 'Invitations');
      }
    }
    // Note: new-entries messages are handled in newEntries$ subscription to avoid duplicate notifications
  }

  /**
   * Classify WebSocket error to determine appropriate response
   */
  private classifyWebSocketError(): WebSocketErrorType {
    if (this.isConfigurationError()) {
      return WebSocketErrorType.CONFIGURATION;
    }

    if (this.isNetworkError()) {
      return WebSocketErrorType.NETWORK;
    }

    if (this.isRuntimeError()) {
      return WebSocketErrorType.RUNTIME;
    }

    return WebSocketErrorType.NETWORK;
  }

  /**
   * Check if this is a configuration-related error
   */
  private isConfigurationError(): boolean {
    return !this.WS_URL || this.WS_URL.includes('undefined') || this.WS_URL === 'undefined';
  }

  /**
   * Check if this is a network/development-related error
   */
  private isNetworkError(): boolean {
    const isNonProduction = !AppConfigModule.appConfig.env.production;
    const isFirstConnection = this.state$.value === WebSocketClientState.INITIAL;

    return isNonProduction || isFirstConnection;
  }

  /**
   * Check if this is a runtime disconnection error
   */
  private isRuntimeError(): boolean {
    return this.state$.value === WebSocketClientState.RECONNECTING;
  }

  /**
   * Handle configuration-related WebSocket errors
   */
  private handleConfigurationError(): void {
    console.log('ℹ️ WebSocket notifications unavailable - configuration issue');
  }

  /**
   * Handle network/development-related WebSocket errors
   */
  private handleNetworkError(event: any): void {
    console.warn('⚠️ WebSocket connection issue (notifications disabled):', event);
  }

  /**
   * Handle runtime WebSocket disconnection errors
   */
  private handleRuntimeError(event: any): void {
    console.error('❌ WebSocket connection lost:', event);
    this.toastr.warning('Live notifications temporarily unavailable', 'Connection Issue');
  }

  private async subscribeToInviteNotifications(): Promise<void> {
    if (!this.client) return;

    try {
      console.log('Getting channels and events for invite subscriptions...');

      // 1. Get existing subscriptions first to avoid duplicates
      const existingSubscriptions = await this.getCurrentSubscriptions();
      const existingEventIds = new Set(existingSubscriptions.map(sub => sub.eventId));

      // Update our local tracking
      existingEventIds.forEach(eventId => this.subscribedEventIds.add(eventId));

      // 2. Get list of channels with their events
      const channels = await this.getChannels();

      // 3. Find invite-related events
      const inviteEventIds: number[] = [];

      channels.forEach(channel => {
        channel.events.forEach(event => {
          // Look for invite-related events
          if (event.name.toLowerCase().includes('invite') ||
            event.displayName.toLowerCase().includes('invite')) {
            inviteEventIds.push(event.eventId);
            console.log(`Found invite event: ${event.name} (ID: ${event.eventId}) in channel: ${channel.name}`);
          }
        });
      });

      // 4. Filter out already subscribed events
      const newEventIds = inviteEventIds.filter(eventId => !this.subscribedEventIds.has(eventId));
      console.log(`Found ${inviteEventIds.length} invite events, ${newEventIds.length} are new subscriptions needed`);

      // 5. Subscribe to each new invite-related event
      for (const eventId of newEventIds) {
        try {
          await this.subscribeToEvent(eventId);
          this.subscribedEventIds.add(eventId);
          console.log(`Successfully subscribed to event ${eventId}`);
        } catch (err) {
          console.warn(`Failed to subscribe to event ${eventId}:`, err);
        }
      }

      console.log(`Subscription complete: ${newEventIds.length} new subscriptions created`);
    } catch (err) {
      console.warn('Failed to setup invite subscriptions:', err);
    }
  }

  // Get list of channels with their events
  private async getChannels(): Promise<Channel[]> {
    if (!this.client) {
      throw new Error('Client not initialized');
    }

    const generatedChannels = await this.client.listChannels();
    return this.convertChannels(generatedChannels);
  }

  // Get current subscriptions
  private async getCurrentSubscriptions(): Promise<Subscription[]> {
    if (!this.client) {
      throw new Error('Client not initialized');
    }

    const generatedSubs = await this.client.listSubscriptions();
    return this.convertSubscriptions(generatedSubs);
  }

  // Subscribe to specific event by eventId
  private async subscribeToEvent(eventId: number, projectId?: number): Promise<void> {
    if (!this.client) {
      throw new Error('Client not initialized');
    }

    try {
      await this.client.subscribe(eventId, projectId);
      this.subscribedEventIds.add(eventId);
    } catch (error) {
      // Handle duplicate subscription attempts gracefully
      if (error instanceof Error && error.message.includes('duplicate key value violates unique constraint')) {
        console.log(`Event ${eventId} already subscribed - ignoring duplicate subscription attempt`);
        this.subscribedEventIds.add(eventId);
        return;
      }
      throw error;
    }
  }

  private handleConnectionFailure(message: string): void {
    this.toastr.error(message, 'Connection Error');
    // Navigate back to project list
    this.router.navigate(['/projects']);
  }

  public dispose(): void {
    this.clearAuthRetry();
    this.subscribedEventIds.clear();
    this.notifiedEntryIds.clear();
    if (this.client) {
      this.client.disconnect();
      this.client = null;
    }
  }

  // Legacy API methods for backward compatibility

  public sendMessage(message: BaseRequest): void {
    console.warn('sendMessage() is deprecated - use type-safe client methods instead');
    if (!this.client || !this.client.isConnected) {
      console.warn('WebSocket is not connected. Message not sent.');
      return;
    }
    this.client.send(message as any);
  }

  public async sendMessageAwait(message: BaseRequest): Promise<void> {
    console.warn('sendMessageAwait() is deprecated - use type-safe client methods instead');
    if (!this.client) {
      throw new Error('Client not initialized');
    }
    await this.client.request(message as any);
  }

  public receiveMessage(): Observable<WebSocketResponse> {
    return this.messages.asObservable();
  }

  public async getCurrentEditor(projectId: number): Promise<void> {
    if (!this.client) {
      throw new Error('Client not initialized');
    }
    await this.client.getCurrentEditor(projectId);
  }

  public async lockEditor(projectId: number): Promise<void> {
    if (!this.client) {
      throw new Error('Client not initialized');
    }
    await this.client.lockEditor(projectId);
  }

  public async unlockEditor(projectId: number): Promise<void> {
    if (!this.client) {
      throw new Error('Client not initialized');
    }
    await this.client.unlockEditor(projectId);
  }

  public async watchProjectData(projectId: number): Promise<void> {
    if (this.client) {
      try {
        await this.client.waitForConnection();
        await this.client.watchProjectData(projectId, true);
        console.log(`Subscribed to project data changes for project ${projectId}`);
      } catch (error) {
        console.warn('Failed to watch project data:', error);
      }
    } else {
      console.warn('watchProjectData: client not initialized');
    }
  }

  public async unwatchProjectData(projectId: number): Promise<void> {
    if (this.client) {
      try {
        await this.client.watchProjectData(projectId, false);
      } catch (error) {
        console.warn('Failed to unwatch project data:', error);
      }
    }
  }

  public async watchJobEvents(projectId: number): Promise<void> {
    if (this.client) {
      try {
        await this.client.waitForConnection();
        await this.client.watchJobEvents(projectId, true);
        console.log(`Subscribed to job events for project ${projectId}`);
      } catch (error) {
        console.warn('Failed to watch job events:', error);
      }
    } else {
      console.warn('watchJobEvents: client not initialized');
    }
  }

  public async unwatchJobEvents(projectId: number): Promise<void> {
    if (this.client) {
      try {
        await this.client.watchJobEvents(projectId, false);
      } catch (error) {
        console.warn('Failed to unwatch job events:', error);
      }
    }
  }

  private async watchEditors(): Promise<void> {
    if (!this.client) return;
    try {
      await this.client.watchEditors(true);
    } catch (error) {
      console.warn('Failed to watch editors:', error);
    }
  }

  // Type conversion helpers (generated → legacy)

  private convertChannels(generated: GeneratedChannel[]): Channel[] {
    return generated.map(ch => ({
      channelId: ch.channelId,
      name: ch.name,
      displayName: ch.displayName,
      description: ch.description,
      events: (ch.events || []).map((ev: GeneratedEvent) => ({
        eventId: ev.eventId,
        channelId: ev.channelId,
        name: ev.name,
        displayName: ev.displayName,
        description: ev.description,
      })),
    }));
  }

  private convertSubscriptions(generated: GeneratedSubscription[]): Subscription[] {
    return generated.map(sub => ({
      subId: sub.subId,
      eventId: sub.eventId,
      userId: sub.userId,
      channelId: sub.channelId,
      channelName: sub.channelName,
      eventName: sub.eventName,
      dateSubscribed: sub.dateSubscribed,
      lastChecked: sub.lastChecked,
    }));
  }

  private convertEntries(generated: GeneratedEntry[]): Entry[] {
    return generated.map(entry => ({
      entryId: entry.entryId,
      subId: entry.subId ?? null,
      entryUserId: entry.entryUserId,
      entryProjectId: entry.entryProjectId ?? 0,
      subProjectId: entry.subProjectId ?? null,
      entryDate: entry.entryDate,
      message: entry.message,
      entrySeen: entry.entrySeen,
      channelId: entry.channelId,
      channelName: entry.channelName,
      eventId: entry.eventId,
      eventName: entry.eventName,
    }));
  }

  private convertCurrentEditor(generated: GeneratedCurrentEditor): CurrentEditor {
    return {
      projectId: generated.projectId,
      userId: generated.userId ?? null,
      username: generated.username ?? null,
      nickname: generated.nickname ?? null,
    };
  }
}
